/**
 * QueueJoinPanel — universal queue-join affordance for the shared
 * availability-cell popover (offer availability tab, catalog, media-unit
 * detail — CU-86cb7ewja).
 *
 * Replaces the single "Jesteś #X w kolejce" text line with:
 *   - every one of the CALLER's own queue entries for this window, rendered
 *     via the shared `ReservationQueueList` (position/client/brand/offer/
 *     dates) — a caller may hold more than one entry under different client
 *     identities;
 *   - a "Dodaj wpis dla innego klienta" affordance that opens client/brand
 *     identity fields to add ANOTHER entry, entirely independent of any
 *     "Zwolnij termin" release action (never rendered here — that stays a
 *     separate, secondary control owned by the caller);
 *   - the FIRST-join affordance when the caller holds no entry yet (see
 *     `queue-first-join-block.tsx`): in an OFFER-scoped context the current
 *     offer identity (offer/client/brand label, resolved server-side) is
 *     shown as a VISIBLE, EDITABLE trigger — "Zmień" switches it to the same
 *     client/brand identity fields used by the non-offer path, so the caller
 *     can redirect the join to a different client before submitting; a
 *     NON-offer context shows those identity fields directly (client
 *     required, brand optional).
 *
 * A join request built here NEVER combines `offerId` with a caller-picked
 * `clientId` — the two identity paths are mutually exclusive per the
 * "Client and brand identity on queue join" requirement's `superRefine`.
 *
 * The shared expiry date control (task 4.3, `reservation-mixed-range-and-target-expiry`)
 * is owned HERE, not by the caller: it renders once, always visible whenever this panel
 * renders (both the first-join and add-another paths read the same state), pre-filled
 * with the standard queued-term suggestion, non-clearable, and bounded to the 60-day
 * horizon — so no joining path on this panel can create a dateless entry.
 *
 * Split for max-lines: shared types + `buildQueueJoinPayload` live in
 * `queue-join-types.ts`, the identity fields sub-form in
 * `queue-identity-fields.tsx`, and the first-join affordance in
 * `queue-first-join-block.tsx`. This file keeps the main exported component
 * and the "add another client" inline form for an existing-entries selection.
 */
import { useState } from 'react';
import { Plus, UserPlus, X } from 'lucide-react';
import { pl } from 'date-fns/locale';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { toDateStr } from '@ooh/ui/lib/date-string';
import {
  addCalendarDays,
  addWorkingDays,
  TTL_HORIZON_DAYS,
  FALLBACK_SUGGESTION_WORKING_DAYS,
} from '@ooh/contracts';
import { ReservationQueueList } from '../components/reservation-queue-list';
import { QueueIdentityFields } from './queue-identity-fields';
import { QueueFirstJoinBlock } from './queue-first-join-block';
import { buildQueueJoinPayload, type QueueJoinPanelProps } from './queue-join-types';

export type {
  QueueJoinClientOption,
  QueueJoinOfferOption,
  QueueJoinIdentity,
  QueueJoinPayload,
  QueueJoinPanelLabels,
  QueueJoinPanelProps,
} from './queue-join-types';
export { QueueOfferIdentityTrigger } from './queue-first-join-block';

export function QueueJoinPanel({
  testId,
  queueListId,
  ownEntries,
  currentUserId,
  permissions,
  identity,
  clientOptions,
  offerOptions = [],
  onJoin,
  onQueueAction,
  isJoinPending,
  labels,
}: QueueJoinPanelProps) {
  const [showForm, setShowForm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [brand, setBrand] = useState('');
  const [offerId, setOfferId] = useState('');
  // Always pre-filled — never blank — shared by every submission path on this panel
  // (task 4.3, `reservation-mixed-range-and-target-expiry`).
  const [ttlDateStr, setTtlDateStr] = useState(() =>
    toDateStr(addWorkingDays(new Date(), FALLBACK_SUGGESTION_WORKING_DAYS)),
  );
  const maxTtlDateStr = toDateStr(addCalendarDays(new Date(), TTL_HORIZON_DAYS));

  function resetForm() {
    setShowForm(false);
    setClientId('');
    setBrand('');
    setOfferId('');
  }

  function submitForm() {
    const payload = buildQueueJoinPayload(clientId, brand, offerId, ttlDateStr);
    if (!payload) return;
    onJoin(payload);
    resetForm();
  }

  const hasOwnEntries = ownEntries.length > 0;
  const showToggle = hasOwnEntries || identity.mode === 'own-reservation';

  return (
    <div className="space-y-2" data-testid={testId}>
      {hasOwnEntries ? (
        <ReservationQueueList
          reservationId={queueListId}
          entries={ownEntries}
          currentUserId={currentUserId}
          permissions={permissions}
          labels={labels.queueListLabels}
          onQueueAction={onQueueAction}
        />
      ) : null}

      <div className="space-y-1">
        <Label htmlFor={`${testId}-ttl-date`} className="text-xs">
          {labels.expiryLabel}
        </Label>
        <DatePickerButton
          id={`${testId}-ttl-date`}
          value={ttlDateStr}
          onChange={setTtlDateStr}
          locale={pl}
          testId={`${testId}-ttl-input`}
          minDate={toDateStr(new Date())}
          maxDate={maxTtlDateStr}
          required
        />
        <p className="text-xs text-muted-foreground">{labels.expiryHint}</p>
      </div>

      {!showToggle ? (
        <QueueFirstJoinBlock
          testId={testId}
          identity={identity}
          clientOptions={clientOptions}
          offerOptions={offerOptions}
          labels={labels}
          isJoinPending={isJoinPending}
          ttlDateStr={ttlDateStr}
          onJoin={onJoin}
        />
      ) : showForm ? (
        <div
          className="space-y-2 rounded-md border border-border/60 p-2"
          data-testid={`${testId}-form`}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold">{labels.newEntryTitle}</p>
            <IconButtonWithTooltip
              icon={X}
              tooltip={labels.cancelLabel}
              variant="ghost"
              size="icon-sm"
              onClick={resetForm}
            />
          </div>
          <QueueIdentityFields
            testId={testId}
            clientOptions={clientOptions}
            offerOptions={offerOptions}
            clientId={clientId}
            brand={brand}
            offerId={offerId}
            onClientChange={setClientId}
            onBrandChange={setBrand}
            onOfferChange={setOfferId}
            labels={labels}
          />
          <Button
            size="xs"
            data-testid={`${testId}-join-btn`}
            disabled={!clientId || isJoinPending}
            onClick={submitForm}
          >
            <UserPlus className="h-3.5 w-3.5" />
            {labels.joinLabel}
          </Button>
        </div>
      ) : (
        <Button
          size="xs"
          variant="outline"
          data-testid={`${testId}-add-another-btn`}
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          {labels.addAnotherClientLabel}
        </Button>
      )}
    </div>
  );
}
